import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import { useApi } from '../../api/useApi.hook';
import { useSession } from '../../app/hooks/useSession.hook';
import { ConfirmationModal } from '../../components/modals/confirmation/confirmationModal.component';
import { useModals } from '../../hooks/useModals.hook';

export function useAiDisclaimer() {
    const api = useApi();
    const queryClient = useQueryClient();
    const modals = useModals();
    const { identity } = useSession();
    const isAcceptedRef = useRef(identity.userFields?.aiDisclaimerAccepted ?? false);

    const saveDisclaimerAccepted = useMutation({
        mutationFn: () => {
            return api.v1.identities.updateMyIdentity({
                ...identity,
                userFields: {
                    ...identity.userFields,
                    aiDisclaimerAccepted: true                    
                }
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['my-identity'] });
        }
    });

    useEffect(() => {
        isAcceptedRef.current = identity.userFields?.aiDisclaimerAccepted ?? false;
    }, [ identity ]);

    // We must use refs to avoid re-rendering the component because the function won't be updated in the editor plugin
    const useAiFeature = useCallback((cb: () => void) => {
        if (isAcceptedRef.current) {
            cb();
            return;
        }

        modals.showModal('ai-disclaimer', ConfirmationModal, {
            title: 'Upozornění na použití funkce umělé inteligence',
            message: (
                <>
                    <p className='mb-4'>Funkce umělé inteligence v této aplikaci slouží pouze jako jazyková pomoc - například k návrhům na zlepšení stylistiky nebo větné skladby. Není určena k vytváření obsahu za uživatele.</p>
                    <p className='mb-4'>Uživatel nese <strong>plnou odpovědnost</strong> za vygenerovaný text, jeho originalitu, věcnou správnost a případný výskyt plagiátů. Provozovatel aplikace <strong>nenese odpovědnost</strong> za obsah vytvořený prostřednictvím AI ani za důsledky jeho použití.</p>
                    <p>Používáním této funkce s tímto výslovně <strong>souhlasíte</strong>.</p>
                </>
            ),
            size: '3xl',
            confirmLabel: 'Souhlasím',
            cancelLabel: 'Nesouhlasím',
            onConfirm: async () => {
                await saveDisclaimerAccepted.mutateAsync();
                cb();

                return true;
            },
        });
    }, [ modals, isAcceptedRef ]);

    return { useAiFeature };
}
